<template>

  <div>

    <unit-list-add-new
      :is-add-new-unit-sidebar-active.sync="isAddNewUnitSidebarActive"
      :role-options="roleOptions"
      @refetch-data="refetchData"
    />
    <unit-list-edit
      :is-edit-unit-sidebar-active.sync="isEditUnitSidebarActive"
      :unit-id.sync="UnitID"
      :unit-name.sync="UnitName"
      :unit-type.sync="UnitType"
    />

    <app-page-shell
      title="Units"
      subtitle="Manage measurement units used across procurement items and transactions."
    >
      <app-list-toolbar
        :per-page="perPage"
        :per-page-options="perPageOptions"
        @update:perPage="perPage = $event"
      >
        <template #search>
          <div class="app-toolbar-actions">
            <b-form-input
              v-model="searchQuery"
              class="app-toolbar-search"
              placeholder="Search..."
            />
            <b-button
              variant="primary"
              @click="isAddNewUnitSidebarActive = true"
            >
              <feather-icon icon="PlusIcon" />
              <span class="align-middle ml-50 text-nowrap">Add Unit</span>
            </b-button>
          </div>
        </template>
      </app-list-toolbar>

      <b-table
        ref="refUnitListTable"
        class="position-relative"
        :items="fetchUnits"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        :busy="loading"
      >
        <template #table-busy>
          <b-skeleton-table
            :rows="perPage || 5"
            :columns="tableColumns ? tableColumns.length : 6"
            :table-props="{ bordered: true, striped: true }"
          />
        </template>

        <!-- Column: Unit name -->
        <template #cell(unit_name)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.unit_name }}</span>
          </div>
        </template>

        <!-- Column: Unit Type -->
        <template #cell(unit_type)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.unit_type }}</span>
          </div>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item @click="editUnit( data.item )">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item @click="deleteUnit( data.item.unit_id )">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>

      </b-table>
      <app-pagination-footer
        :meta="dataMeta"
        :current-page="currentPage"
        :total-rows="totalUnites"
        :per-page="perPage"
        @update:currentPage="currentPage = $event"
      />
    </app-page-shell>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
  BBadge, BDropdown, BDropdownItem, BPagination, BSkeletonTable,
} from 'bootstrap-vue'
import axios from 'axios'
import vSelect from 'vue-select'
import store from '@/store'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ref, onUnmounted } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'
// import UnitesListFilters from './UnitesListFilters.vue'
import apiEndpoints from '@/constants/apiEndpoints'
import AppPageShell from '@/views/apps/components/AppPageShell.vue'
import AppListToolbar from '@/views/apps/components/AppListToolbar.vue'
import AppPaginationFooter from '@/views/apps/components/AppPaginationFooter.vue'
import UnitList from './UnitList'
import unitModule from '../unitModule'
import UnitListAddNew from './UnitAddNew.vue'
import UnitListEdit from './UnitEdit.vue'

const userRole = ''
const UnitData = null
export default {
  components: {
    // UnitesListFilters,
    UnitListEdit,
    UnitListAddNew,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSkeletonTable,

    vSelect,
    AppPageShell,
    AppListToolbar,
    AppPaginationFooter,
  },
  mounted() {
    this.userRole = this.$cookies.get('UserRole')
    this.$root.$on('refreshTable', text => {
    if (this.$refs.refUnitListTable) this.$refs.refUnitListTable.refresh()
  })
  },
  setup() {
    const BRANCH_APP_STORE_MODULE_NAME = 'app-unit'

    // Register module
    if (!store.hasModule(BRANCH_APP_STORE_MODULE_NAME)) store.registerModule(BRANCH_APP_STORE_MODULE_NAME, unitModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(BRANCH_APP_STORE_MODULE_NAME)) store.unregisterModule(BRANCH_APP_STORE_MODULE_NAME)
    })

    const isAddNewUnitSidebarActive = ref(false)
    const isEditUnitSidebarActive = ref(false)
    const UnitID = ref('')
    const UnitName = ref('')
    const UnitType = ref('')
    const statusOptions = [
      { label: 'Pending', value: 'pending' },
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ]

    const {
      fetchUnits,
      fetchRoles,
      tableColumns,
      perPage,
      currentPage,
      totalUnites,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUnitListTable,
      refetchData,

      // UI
      resolveUnitRoleVariant,
      resolveUnitRoleIcon,
      resolveUnitStatusVariant,

      // Extra Filters
      roleFilter,
      statusFilter,
      loading,
    } = UnitList()

    const roleOptions = [
      { label: 'Admin', value: 'admin' },
      { label: 'Author', value: 'author' },
      { label: 'Editor', value: 'editor' },
      { label: 'Maintainer', value: 'maintainer' },
      { label: 'Subscriber', value: 'subscriber' },
    ]

    return {

      // Sidebar
      UnitID,
      UnitName,
      UnitType,
      isEditUnitSidebarActive,
      isAddNewUnitSidebarActive,
      userRole,
      UnitData,
      fetchUnits,
      fetchRoles,
      tableColumns,
      perPage,
      currentPage,
      totalUnites,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUnitListTable,
      refetchData,

      // Filter
      avatarText,

      // UI
      resolveUnitRoleVariant,
      resolveUnitRoleIcon,
      resolveUnitStatusVariant,

      roleOptions,
      statusOptions,

      // Extra Filters
      roleFilter,
      statusFilter,
      loading,
    }
  },
  methods: {
      deleteUnit(id) {
        const userToken = this.$cookies.get('userToken')
const headers = {
  'Content-Type': 'application/json',
  Authorization: `Bearer ${userToken}`,
}
        const url = `${process.env.VUE_APP_API_URL}${apiEndpoints.API_DELETE_UNIT}/${id}`
          axios
              .get(url, { headers })
              .then(response => {
                if (response.data.success === true) {
                  if (this.$refs.refUnitListTable) this.$refs.refUnitListTable.refresh()
                  this.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                      title: 'Congratulation',
                      icon: 'CoffeeIcon',
                      variant: 'success',
                      text: 'Your data has been deleted!',
                    },
                  })
                } else {
                  this.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                      title: 'Notification',
                      icon: 'BellIcon',
                      variant: 'danger',
                      text: 'Something went wrong',
                    },
                  })
                }
              })
              .catch(err => {

              })
      },
      editUnit(data) {
        const parsed = data
        this.UnitID = parsed.unit_id
        this.UnitName = parsed.unit_name
        this.UnitType = parsed.unit_type
          this.isEditUnitSidebarActive = true
      },
  },
}
</script>

<style lang="scss" scoped>
.app-toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: .75rem;
  min-width: 0;
}

.app-toolbar-search {
  width: 260px;
}

@media (max-width: 991.98px) {
  .app-toolbar-actions {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }

  .app-toolbar-search {
    width: 100%;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
